import type { DraggedBlock } from '#layers/block-engine/composables/block-editor/drag';
import { onUnmounted } from 'vue';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';

/**
 * Context passed to overrideBlockDrag callbacks
 */
export interface OverrideBlockDragContext {
	/**
	 * The block being dragged
	 */
	draggedBlock: DraggedBlock;
	/**
	 * The child ID of this block instance
	 */
	childId: string;
	/**
	 * Current mouse position
	 */
	mouse: { x: number; y: number };
	/**
	 * Bounds of this block instance
	 */
	bounds: { x: number; y: number; width: number; height: number };
}

/**
 * Result from overrideBlockDrag.onDragOver
 */
export interface OverrideBlockDragResult {
	/**
	 * Whether the block wants to capture the drag
	 * When true, the normal block builder drag target is not set
	 */
	captured: boolean;
}

/**
 * Drop context passed to overrideBlockDrag.onDrop
 */
export interface OverrideBlockDragDropContext extends OverrideBlockDragContext {
	/**
	 * Whether alt key was held (for duplication)
	 */
	altKey: boolean;
}

/**
 * Configuration for custom block drag handling
 */
export interface OverrideBlockDragConfig {
	/**
	 * Called when a block is dragged over this block's bounds.
	 * Return { captured: true } to take over drag handling.
	 */
	onDragOver: (context: OverrideBlockDragContext) => OverrideBlockDragResult;
	/**
	 * Called when the drag ends while captured by this block.
	 */
	onDrop: (context: OverrideBlockDragDropContext) => void;
	/**
	 * Called when the drag leaves this block's bounds while captured.
	 */
	onDragLeave?: (context: OverrideBlockDragContext) => void;
}

/**
 * Register a drag handler for a specific block instance.
 * Call this in the block's setup to enable drag capture for that instance.
 * The handler is scoped to the block editor instance via the editor context.
 */
export function useBlockDragHandler(childId: string, config: OverrideBlockDragConfig) {
	const { drag } = useBlockEditorContext();
	drag.dragHandlerRegistry.set(childId, config);

	onUnmounted(() => {
		drag.dragHandlerRegistry.delete(childId);
	});
}
